import { useEffect, useState } from 'react';
import axios from 'axios';
import vopayApi from '@/services/api.config';
import { useClientData } from '@/providers/ClientData.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';

const { api } = vopayApi;

export default function useLabels(customerAccountID?: string) {
  const {
    response: { clientAccountsEnabled },
  } = useClientData();

  const { permissions } = usePermissions();

  const source = axios.CancelToken.source();
  const [labels, setLabels] = useState<string[]>([]);
  const [fetchingLabels, setFetching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [labelsLoaded, setLoaded] = useState(false);
  const alertDialog = useAlertDialog();

  useEffect(() => {
    fetchLabels().then(() => setLoaded(true));

    return () => {
      source.cancel();
    };
  }, []);

  async function fetchLabels() {
    if (clientAccountsEnabled && !permissions.ViewClientAccounts) return;

    try {
      setFetching(true);

      const res = clientAccountsEnabled
        ? await api.fetchClientAccountLabels(
            {
              ClientAccountID: customerAccountID,
            },
            source,
          )
        : await api.fetchContactLabels(
            {
              ContactID: customerAccountID,
            },
            source,
          );

      if (!res.Success) {
        alertDialog.error('Failed to fetch client account labels', res.ErrorMessage, {
          abortLabel: 'Close',
        });

        return;
      }

      setLabels(res.Labels.map((label) => label.Label));

      return res.Labels;
    } finally {
      setFetching(false);
    }
  }

  async function addLabel(label: string, _customerAccountID?: string) {
    const id = _customerAccountID || customerAccountID;

    if (!id) throw new Error('Account ID is required');

    try {
      setSubmitting(true);

      const res = clientAccountsEnabled
        ? await api.addClientAccountLabel(
            {
              ClientAccountID: id,
              Label: label,
            },
            source,
          )
        : await api.addContactLabel({
            ContactID: id,
            Label: label,
          });

      if (!res.Success) {
        alertDialog.error('Failed to add ' + label, res.ErrorMessage, {
          abortLabel: 'Close',
        });

        return res;
      }

      await fetchLabels();

      return res;
    } finally {
      setSubmitting(false);
    }
  }

  async function deleteLabel(label: string, _customerAccountID?: string) {
    const id = _customerAccountID || customerAccountID;

    if (!id) throw new Error('Account ID is required');

    try {
      setSubmitting(true);

      const res = clientAccountsEnabled
        ? await api.deleteClientAccountLabel(
            {
              ClientAccountID: id,
              Label: label,
            },
            source,
          )
        : await api.deleteContactLabel({
            ContactID: id,
            Label: label,
          });

      if (!res.Success) {
        alertDialog.error('Failed to add ' + label, res.ErrorMessage, {
          abortLabel: 'Close',
        });

        return;
      }

      await fetchLabels();

      return res;
    } finally {
      setSubmitting(false);
    }
  }

  return {
    labelsLoaded,
    fetchingLabels,
    submitting,
    labels,
    fetchLabels,
    addLabel,
    deleteLabel,
  };
}
